import { TAG_TYPE } from "../../enums";
import {
  ItemNutritionType,
  ItemTagType,
  ItemType,
  SubItemType,
} from "../../types";
import { getLocalizedString } from "../localization/localization";

/**
 * Labels of the tags of one kind, resolved for the active language and in the
 * order the menu API returns them (`item_tag.order`).
 *
 * A tag with no copy in any language is skipped, as is one whose kind we do not
 * recognise — a kind added server-side should not leak onto the menu before the
 * guest knows how to draw it. An untyped tag is treated as a plain LABEL, which
 * is what the column defaults to.
 */
const labelsOfType = (
  tags: ItemTagType[] | undefined,
  type: TAG_TYPE
): string[] => {
  if (!tags?.length) {
    return [];
  }

  return tags
    .filter((tag) => (tag.type ?? TAG_TYPE.LABEL) === type)
    .map((tag) => getLocalizedString(tag.label)?.trim())
    .filter((label): label is string => !!label);
};

/**
 * The chips to render for an item.
 *
 * Admin caps items at one tag today, but `item_tag` is a many-to-many and the
 * app renders every tag it is given, so do the same rather than silently
 * dropping the rest.
 */
export const getItemTagLabels = (
  item?: Pick<ItemType, "tags"> | null
): string[] => labelsOfType(item?.tags, TAG_TYPE.LABEL);

type ItemNutritionDisplay = {
  calories?: number;
  allergens?: string;
};

/**
 * Calorie count and allergen list for the item page, resolved per variant.
 *
 * The selected variant wins whenever it carries its own value — a Small Combo
 * and a Large Combo differ — otherwise the item's is used. The variant's tags
 * are absent rather than empty when it has none, which is precisely how the API
 * says "same as the item".
 */
export const getItemNutrition = (
  item?: (ItemNutritionType & Pick<ItemType, "tags">) | null,
  selectedSubItem?: SubItemType | null
): ItemNutritionDisplay => {
  const calories = selectedSubItem?.calories ?? item?.calories;

  const variantAllergens = labelsOfType(
    selectedSubItem?.tags,
    TAG_TYPE.ALLERGENS
  );
  const allergens = variantAllergens.length
    ? variantAllergens
    : labelsOfType(item?.tags, TAG_TYPE.ALLERGENS);

  return {
    calories: typeof calories === "number" ? calories : undefined,
    allergens: allergens.length ? allergens.join(", ") : undefined,
  };
};
